<template>
  <div class="template-list">
    <!-- Filters -->
    <div class="filters">
      <select v-model="filterStatus" @change="loadTemplates" class="filter-select">
        <option value="">{{ $t('bo.sessionTemplates.allStatuses') }}</option>
        <option value="draft">{{ $t('bo.sessionTemplates.statusDraft') }}</option>
        <option value="published">{{ $t('bo.sessionTemplates.statusPublished') }}</option>
        <option value="archived">{{ $t('bo.sessionTemplates.statusArchived') }}</option>
      </select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

    <!-- Error -->
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <!-- Empty -->
    <div v-else-if="templates.length === 0" class="empty">
      {{ $t('bo.sessionTemplates.noTemplates') }}
    </div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.sessionTemplates.colTitle') }}</th>
          <th>{{ $t('bo.sessionTemplates.colStatus') }}</th>
          <th>{{ $t('bo.sessionTemplates.colDuration') }}</th>
          <th>{{ $t('bo.sessionTemplates.colTags') }}</th>
          <th>{{ $t('bo.sessionTemplates.colCreated') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="tpl in templates" :key="tpl.id">
          <td class="col-title">
            <div class="template-title">{{ tpl.title }}</div>
            <div v-if="tpl.description" class="template-desc">{{ truncate(tpl.description, 80) }}</div>
          </td>
          <td>
            <span :class="['badge', `badge-${tpl.status}`]">{{ tpl.status }}</span>
          </td>
          <td>{{ tpl.estimated_duration ? `${tpl.estimated_duration} min` : '—' }}</td>
          <td>
            <span v-for="tag in (tpl.tags || []).slice(0, 3)" :key="tag" class="tag">{{ tag }}</span>
            <span v-if="(tpl.tags || []).length > 3" class="tag-more">+{{ tpl.tags.length - 3 }}</span>
          </td>
          <td>{{ formatDate(tpl.created_at) }}</td>
          <td class="actions">
            <button @click="$emit('edit', tpl.id)" class="btn-action btn-edit">{{ $t('bo.common.edit') }}</button>
            <button
              v-if="tpl.status === 'draft'"
              @click="handlePublish(tpl)"
              class="btn-action btn-publish"
              :disabled="actionLoading === tpl.id"
            >{{ $t('bo.sessionTemplates.publish') }}</button>
            <button
              v-if="tpl.status !== 'archived'"
              @click="handleArchive(tpl)"
              class="btn-action btn-archive"
              :disabled="actionLoading === tpl.id"
            >{{ $t('bo.sessionTemplates.archive') }}</button>
            <button
              v-if="tpl.status === 'draft'"
              @click="handleDelete(tpl)"
              class="btn-action btn-delete"
              :disabled="actionLoading === tpl.id"
            >{{ $t('bo.common.delete') }}</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import sessionTemplateService from '@/services/sessionTemplateService'

const { t } = useI18n()

const emit = defineEmits(['edit', 'view'])

const templates = ref([])
const loading = ref(false)
const error = ref('')
const filterStatus = ref('')
const actionLoading = ref(null)

const loadTemplates = async () => {
  loading.value = true
  error.value = ''
  try {
    const params = {}
    if (filterStatus.value) params.status = filterStatus.value
    const res = await sessionTemplateService.getTemplates(params)
    templates.value = res.data || []
  } catch (e) {
    error.value = t('bo.sessionTemplates.failedLoad')
  } finally {
    loading.value = false
  }
}

const handlePublish = async (tpl) => {
  if (!confirm(t('bo.sessionTemplates.confirmPublish', { title: tpl.title }))) return
  actionLoading.value = tpl.id
  try {
    await sessionTemplateService.publishTemplate(tpl.id)
    await loadTemplates()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.sessionTemplates.failedPublish'))
  } finally {
    actionLoading.value = null
  }
}

const handleArchive = async (tpl) => {
  if (!confirm(t('bo.sessionTemplates.confirmArchive', { title: tpl.title }))) return
  actionLoading.value = tpl.id
  try {
    await sessionTemplateService.archiveTemplate(tpl.id)
    await loadTemplates()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.sessionTemplates.failedArchive'))
  } finally {
    actionLoading.value = null
  }
}

const handleDelete = async (tpl) => {
  if (!confirm(t('bo.sessionTemplates.confirmDelete', { title: tpl.title }))) return
  actionLoading.value = tpl.id
  try {
    await sessionTemplateService.deleteTemplate(tpl.id)
    await loadTemplates()
  } catch (e) {
    alert(e.response?.data?.message || t('bo.sessionTemplates.failedDelete'))
  } finally {
    actionLoading.value = null
  }
}

const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '…' : str

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

defineExpose({ loadTemplates })
onMounted(loadTemplates)
</script>

<style scoped>
.template-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td {
  border-bottom: none;
}

.col-title {
  max-width: 280px;
}

.template-title {
  font-weight: 600;
  color: #1f2937;
}

.template-desc {
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-draft { background: #fef3c7; color: #92400e; }
.badge-published { background: #d1fae5; color: #065f46; }
.badge-archived { background: #f3f4f6; color: #6b7280; }

.tag {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 9999px;
  font-size: 0.75rem;
  margin-right: 0.25rem;
}

.tag-more {
  font-size: 0.75rem;
  color: #6b7280;
}

.actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  white-space: nowrap;
}

.btn-action {
  padding: 0.3rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-edit { border-color: #3b82f6; color: #3b82f6; background: white; }
.btn-edit:hover:not(:disabled) { background: #eff6ff; }

.btn-publish { border-color: #10b981; color: #10b981; background: white; }
.btn-publish:hover:not(:disabled) { background: #ecfdf5; }

.btn-archive { border-color: #f59e0b; color: #f59e0b; background: white; }
.btn-archive:hover:not(:disabled) { background: #fffbeb; }

.btn-delete { border-color: #ef4444; color: #ef4444; background: white; }
.btn-delete:hover:not(:disabled) { background: #fef2f2; }
</style>
